import React, { useEffect, useRef, useState } from "react";
import {
  BEST_PRACTICES,
  COMMON_MISTAKES,
  CONFIDENCE_LEVELS,
  FUTURE_LEARNING,
  OPPORTUNITY_SCORE_EXAMPLE,
  SCANNER_GUIDE_SECTIONS,
  SCANNER_SUMMARIES,
  SIMULATED_SETUPS,
} from "../scannerGuide/ScannerFeatureGuideContent";
import "./ScannerFeatureGuide.css";

export default function ScannerFeatureGuide({ onClose }) {
  const [openSection, setOpenSection] = useState("scanner-overview");
  const titleRef = useRef(null);

  useEffect(() => {
    titleRef.current?.focus();
  }, []);

  return (
    <aside
      id="scanner-feature-guide"
      className="scannerFeatureGuide"
      aria-labelledby="scanner-feature-guide-title"
    >
      <header className="scannerGuideHeader">
        <div>
          <span>EDUCATIONAL REFERENCE</span>
          <h2 id="scanner-feature-guide-title" ref={titleRef} tabIndex="-1">
            Scanner Feature Guide
          </h2>
          <p>
            Learn what each review mode highlights. Examples are simulated and
            educational only.
          </p>
        </div>
        <div className="scannerGuideHeaderActions">
          <strong>NO EXECUTION</strong>
          <button type="button" onClick={onClose} aria-label="Close Feature Guide">
            Close
          </button>
        </div>
      </header>

      <div className="scannerGuideSafety" role="note">
        SIMULATED / EDUCATIONAL · Informational review only · No prediction,
        guarantee, brokerage connection, routing, or automatic execution
      </div>

      <div className="scannerGuideAccordion">
        {SCANNER_GUIDE_SECTIONS.map((section) => {
          const expanded = openSection === section.id;
          return (
            <section className="scannerGuideSection" key={section.id}>
              <h3>
                <button
                  type="button"
                  aria-expanded={expanded}
                  aria-controls={`scanner-guide-panel-${section.id}`}
                  onClick={() =>
                    setOpenSection((current) =>
                      current === section.id ? "" : section.id,
                    )
                  }
                >
                  <span>{section.title}</span>
                  <i aria-hidden="true">{expanded ? "−" : "+"}</i>
                </button>
              </h3>
              <div
                id={`scanner-guide-panel-${section.id}`}
                className="scannerGuideSectionContent"
                hidden={!expanded}
              >
                <GuideSectionContent sectionId={section.id} />
              </div>
            </section>
          );
        })}
      </div>
    </aside>
  );
}

function GuideSectionContent({ sectionId }) {
  switch (sectionId) {
    case "scanner-overview":
      return (
        <div className="scannerGuideCardGrid">
          {SCANNER_SUMMARIES.map((scanner) => (
            <article className="scannerGuideInfoCard" key={scanner.name}>
              <span>SCANNER MODE</span>
              <h4>{scanner.name}</h4>
              <p>{scanner.description}</p>
            </article>
          ))}
        </div>
      );
    case "when-to-use":
      return (
        <div className="scannerGuideUseList">
          {SCANNER_SUMMARIES.map((scanner) => (
            <div key={scanner.name}>
              <strong>{scanner.name}</strong>
              <p>{scanner.whenToUse}</p>
            </div>
          ))}
        </div>
      );
    case "opportunity-score":
      return <OpportunityScoreGuide />;
    case "ai-confidence":
      return <ConfidenceGuide />;
    case "simulated-setups":
      return <SimulatedSetupGuide />;
    case "best-practices":
      return <BulletGuide items={BEST_PRACTICES} />;
    case "common-mistakes":
      return <BulletGuide items={COMMON_MISTAKES} warning />;
    case "future-learning":
      return (
        <div className="scannerFutureGrid">
          {FUTURE_LEARNING.map((item) => (
            <div key={item}>
              <span>COMING LATER</span>
              <strong>{item}</strong>
            </div>
          ))}
        </div>
      );
    default:
      return null;
  }
}

function OpportunityScoreGuide() {
  const total = OPPORTUNITY_SCORE_EXAMPLE.reduce(
    (sum, factor) => sum + factor.points,
    0,
  );

  return (
    <div className="scannerScoreGuide">
      <p>
        Opportunity Score summarizes multiple setup factors into a 0–100
        review aid. Existing scanner calculations remain mode-specific.
      </p>
      <div className="scannerScoreExample" aria-label="Simulated Opportunity Score example">
        <header>
          <div>
            <span>SIMULATED EXAMPLE</span>
            <strong>Educational breakout setup</strong>
          </div>
          <b>{total}/100</b>
        </header>
        <dl>
          {OPPORTUNITY_SCORE_EXAMPLE.map((factor) => (
            <div key={factor.label}>
              <dt>{factor.label}</dt>
              <dd>
                <span>{factor.explanation}</span>
                <strong>{factor.points}/20</strong>
              </dd>
            </div>
          ))}
        </dl>
      </div>
    </div>
  );
}

function ConfidenceGuide() {
  return (
    <div>
      <div className="scannerConfidenceGrid">
        {CONFIDENCE_LEVELS.map((level) => (
          <article key={level.label}>
            <span>{level.range}</span>
            <h4>{level.label}</h4>
            <p>{level.description}</p>
          </article>
        ))}
      </div>
      <p className="scannerGuideCaution">
        AI Confidence is informational only. It is not a prediction, promise,
        or guarantee of a trading outcome.
      </p>
    </div>
  );
}

function SimulatedSetupGuide() {
  return (
    <div className="scannerSetupGrid">
      {SIMULATED_SETUPS.map((setup) => (
        <article key={setup.type}>
          <header>
            <span>SIMULATED EXAMPLE</span>
            <strong>{setup.type}</strong>
          </header>
          <h4>{setup.symbol} · {setup.setup}</h4>
          <dl>
            <div><dt>Demo trigger</dt><dd>{setup.trigger}</dd></div>
            <div><dt>Demo risk</dt><dd>{setup.risk}</dd></div>
            <div><dt>Review focus</dt><dd>{setup.focus}</dd></div>
          </dl>
        </article>
      ))}
    </div>
  );
}

function BulletGuide({ items, warning = false }) {
  return (
    <ul className={warning ? "scannerGuideList warning" : "scannerGuideList"}>
      {items.map((item) => <li key={item}>{item}</li>)}
    </ul>
  );
}
